<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import KpiConfigurationService from '@/services/quality/kpiConfiguration.service'
import { VDataTableServer } from 'vuetify/lib/labs/VDataTable/index.mjs'

definePage({
  meta: {
    subject: 'quality.menu',
    action: 'master_kpi_configuration',
  },
})

const headers = [
  { title: 'Code', key: 'code', sortable: false },
  { title: 'Title', key: 'title' },
  { title: 'Framework', key: 'framework.name', sortable: false },
  { title: 'Chapter', key: 'chapter.name', sortable: false },
  { title: 'Frequency', key: 'frequency' },
  { title: 'Status', key: 'status', sortable: false },
  { title: 'Actions', key: 'actions', sortable: false },
]

const search = ref('')
const debouncedSearch = refDebounced(search, 500)
const itemsPerPage = ref(10)
const pageNumber = ref(1)
const sortBy = ref()
const orderBy = ref()

const {
  isFetching,
  data: kpiConfigurationsData,
  execute: fetchKpiConfigurations,
} = useApi(createUrl('/incident/kpi-configurations', {
  query: {
    paged: 1,
    q: debouncedSearch,
    itemsPerPage,
    page: pageNumber,
    sort: sortBy,
    order_by: orderBy,
    with: ['framework', 'chapter'],
  },
}))

const kpiConfigurations = computed(() => kpiConfigurationsData.value?.data ?? kpiConfigurationsData.value ?? [])
const totalKpiConfigurations = computed(() => kpiConfigurationsData.value?.meta?.total ?? kpiConfigurationsData.value?.length ?? 0)

// Delete
const isConfirmDialogVisible = ref(false)
const deletingKpiConfiguration = ref(null)
const refConfirmDialog = ref()

const showConfirmDialog = item => {
  deletingKpiConfiguration.value = item
  isConfirmDialogVisible.value = true
}

const deleteKpiConfiguration = async (confirm, data) => {
  if (data?.id && confirm) {
    await KpiConfigurationService.destroy(
      data.id,
      () => {
        refConfirmDialog.value.onSuccess()
        isConfirmDialogVisible.value = false
        fetchKpiConfigurations()
      },
      () => {
        refConfirmDialog.value.onError()
      },
    )
  }
}

watch(isConfirmDialogVisible, value => {
  if (!value) deletingKpiConfiguration.value = null
})
</script>

<template>
  <VCard
    variant="flat"
    class="border"
  >
    <VCardText class="d-flex flex-wrap py-4 gap-4">
      <div style="inline-size: 12rem;">
        <AppTextField
          v-model="search"
          placeholder="Search"
          density="compact"
        />
      </div>

      <VSpacer />

      <div class="d-flex align-center gap-4">
        <VBtn
          v-if="$can('create', 'quality.kpi_configuration')"
          variant="flat"
          prepend-icon="tabler-plus"
          :to="{ name: 'app-quality-master-kpi-configuration-setup' }"
        >
          Create KPI Configuration
        </VBtn>

        <IconBtn
          variant="tonal"
          class="rounded"
          :disabled="isFetching"
          @click="fetchKpiConfigurations"
        >
          <VIcon icon="tabler-refresh" />
        </IconBtn>
      </div>
    </VCardText>

    <VDivider />

    <VDataTableServer
      v-model:items-per-page="itemsPerPage"
      v-model:page="pageNumber"
      :items-length="totalKpiConfigurations"
      :items="kpiConfigurations"
      :headers="headers"
      fixed-header
      class="text-no-wrap"
      :loading="isFetching"
    >
      <template #item.code="{ item }">
        <span class="font-weight-medium">{{ item.code ?? 'N/A' }}</span>
      </template>

      <template #item.framework.name="{ item }">
        <VChip
          v-if="item.framework?.name"
          size="small"
          color="primary"
          variant="tonal"
        >
          {{ item.framework.name }}
        </VChip>
        <span
          v-else
          class="text-medium-emphasis"
        >N/A</span>
      </template>

      <template #item.chapter.name="{ item }">
        <VChip
          v-if="item.chapter?.name"
          size="small"
          color="info"
          variant="tonal"
        >
          {{ item.chapter.name }}
        </VChip>
        <span
          v-else
          class="text-medium-emphasis"
        >N/A</span>
      </template>

      <template #item.frequency="{ item }">
        <span class="text-capitalize">{{ item.frequency ?? 'N/A' }}</span>
      </template>

      <template #item.status="{ item }">
        <VChip
          size="small"
          :color="item.status ? 'success' : 'error'"
          variant="tonal"
        >
          {{ item.status ? 'Active' : 'Inactive' }}
        </VChip>
      </template>

      <template #item.actions="{ item }">
        <IconBtn
          v-if="$can('update', 'quality.kpi_configuration')"
          :to="{ name: 'app-quality-master-kpi-configuration-setup', params: { id: item.id } }"
        >
          <VIcon icon="tabler-edit" />
        </IconBtn>
        <IconBtn
          v-if="$can('delete', 'quality.kpi_configuration')"
          @click="showConfirmDialog(item)"
        >
          <VIcon icon="tabler-trash" />
        </IconBtn>
      </template>
    </VDataTableServer>
  </VCard>

  <ConfirmDialog
    ref="refConfirmDialog"
    v-model:isDialogVisible="isConfirmDialogVisible"
    confirmation-question="Are you sure you want to delete this KPI Configuration?"
    confirm-msg="KPI Configuration Deleted."
    confirm-title="Deleted!"
    cancel-title="Error!"
    cancel-msg="Something Went Wrong."
    :is-confirm-visible="false"
    :is-error-visible="false"
    :data="deletingKpiConfiguration"
    @confirm="deleteKpiConfiguration"
  />
</template>

<style>
.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>
